import { debounceTime, takeUntil } from 'rxjs/operators';
import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { PhActionListItem } from '@payhoa/ui-kit/elements';
import { PlaidInstitutionSearchResult, PlaidService } from '../../../../../services/plaid.service';
import { Organization } from '../../../../../models/entities/organization';
import { OrganizationService } from '../../../../../services/organization/organization.service';
import { Subject } from 'rxjs';
import { SharedModule } from '../../../../shared/shared.module';

export type PlaidSearchProduct = 'auth' | 'transactions';

export interface PlaidSearchDialogResult
{
    success: boolean;
    selection?: PlaidInstitutionSearchResult;
}

@UntilDestroy()
@Component({
    selector: 'app-plaid-search-dialog',
    templateUrl: './plaid-search-dialog.component.html',
    standalone: true,
    imports: [
        SharedModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PlaidSearchDialogComponent implements OnInit, OnChanges
{
    @Input() open = false;
    @Input() product: PlaidSearchProduct = 'transactions';
    @Output() readonly closed = new EventEmitter<PlaidSearchDialogResult>();

    public institutionSearchControl: UntypedFormControl = new UntypedFormControl();
    public institutions: PlaidInstitutionSearchResult[] = [];
    public preferencesLoaded: boolean = false;
    public manualAddEnabled: boolean = false;
    private cancelInFlight = new Subject();

    constructor(
            private _plaidService: PlaidService,
            private _organizationsService: OrganizationService,
    ) {
    }

    get institutionItems(): PhActionListItem[] {
        return this.institutions.map(inst => ({value: inst.institutionId, label: inst.name}));
    }

    ngOnInit(): void {
        this.institutionSearchControl.valueChanges.pipe(debounceTime(200), untilDestroyed(this)).subscribe(
                (searchTerm) => {
                    this.searchInstitutions(searchTerm);
                },
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.open && this.open) {
            this.reset();
        }
    }

    public searchInstitutions(searchTerm: string) {
        this.cancelInFlight.next(true);
        if ((searchTerm || '').trim() !== '') {
            this._plaidService.searchInstitutions(searchTerm, this.product).pipe(takeUntil(this.cancelInFlight)).subscribe(
                    (data: PlaidInstitutionSearchResult[]) => {
                        this.institutions = data;
                    },
            );
        } else {
            this.institutions = [];
        }
    }

    public closeDialog() {
        this.cancelInFlight.next(true);
        this.closed.emit(<PlaidSearchDialogResult>{
            success: false,
        });
    }

    public selectInstitutionById(institutionId: string) {
        this.selectInstitution(this.institutions.find(inst => inst.institutionId === institutionId) || null);
    }

    public selectInstitution(institution: PlaidInstitutionSearchResult | null) {
        this.cancelInFlight.next(true);
        this.closed.emit(<PlaidSearchDialogResult>{
            success: true,
            selection: institution,
        });
    }

    //noinspection JSMethodCanBeStatic
    public cancelEvent($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();
    }

    private reset() {
        this.cancelInFlight.next(true);
        this.institutionSearchControl.setValue('', {emitEvent: false});
        this.institutions = [];
        this.preferencesLoaded = false;
        if (this.product === 'auth') {
            this._organizationsService
                    .getOrganizationById(null, ['preferences'])
                    .pipe(untilDestroyed(this))
                    .subscribe(
                            (org: Organization) => {
                                this.preferencesLoaded = true;
                                this.manualAddEnabled = org.preferences.manualBankAccountAddingEnabled;
                            },
                    );
        }
    }
}
